import { Injectable, inject } from "@angular/core";

import { map, mergeMap, tap } from "rxjs";

import {
  NgxsAfterBootstrap,
  Action,
  Selector,
  State,
  StateContext,
  StateToken,
  createSelector,
  Store,
} from "@ngxs/store";
import { patch } from "@ngxs/store/operators";
import { upsertItem } from "../../operators/ngxs-upsertItem";

import { AuthState } from "@dis/auth";
import { BaseCollectionsState } from "../../../states/base.state";
import { appAfterBootstrap } from "../../operators/afterBootstrap";

import { TableQuery, TableState } from "../../../components/tables";

/**
 *  ~~~~ ONLY EDIT BETWEEN THESE COMMENTS ~~~~
 */

import { ArrayDate, COLLECTIONS, Job as Model } from "@dis/models";
import { JobsSheetStore as SheetStore } from "../services/sheetstore";
import { JobsStateActions as StateActions } from "./actions";

const COLLECTION = COLLECTIONS.JOBS;

export { type StateModel as JobsStateModel, STATE_TOKEN as JOBS_STATE_TOKEN, StateStore as JobsState };

/**
 * ^^^^ ONLY EDIT BETWEEN THESE COMMENTS ^^^^
 */

const STATE_TOKEN = new StateToken<StateModel>(`${COLLECTION.toLowerCase()}sState`);

interface StateModel {
  loading: boolean;
  items: Model[];
  filterText: string | null;
}

@State({
  name: STATE_TOKEN,
  defaults: {
    loading: false,
    items: [],
    filterText: null,
  },
})
@Injectable()
class StateStore extends BaseCollectionsState implements NgxsAfterBootstrap {
  private readonly db = inject(SheetStore);
  private readonly store = inject(Store);

  /**
   *  DO NOT ADD SELCTORS HERE USE THE "stateName.selectors.ts file"
   */

  @Selector([STATE_TOKEN])
  static items(state: StateModel) {
    return state.items;
  }

  @Selector([StateStore.items])
  static count(items: Model[]) {
    return items.length;
  }

  @Selector([StateStore.items, TableState.tableQuery(COLLECTION)])
  static filteredByProperties(items: Model[], { filterByProperties }: TableQuery) {
    console.log({ filterByProperties });
    if (!filterByProperties) return [...items];

    const keys = Object.keys(filterByProperties);
    keys.every((key) => console.log({ key, value: filterByProperties[key] }));

    return [...items.filter((item) => keys.every((key) => item[key as keyof Model] === filterByProperties[key]))];
  }

  @Selector([StateStore.items, TableState.tableQuery(COLLECTION)])
  static tableItems(items: Model[], { active, direction }: TableQuery) {
    return this.sortByActive(items, active, direction);
  }

  @Selector([StateStore.tableItems, TableState.tableQuery(COLLECTION)])
  static filtered(items: Model[], { filterText }: TableQuery) {
    return filterText ? [...items.filter((item) => this.filterPredicate(item, filterText))] : [...items];
  }

  static getById(uid: string) {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.uid === uid)[0];
    });
  }

  /**
   * @deprecated moving to JobsSelectors.getByJobNumber to be lazy for signals
   * @param jobNumber
   * @returns
   */
  static getByJobNumber(jobNumber: string) {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.jobNumber === jobNumber)[0];
    });
  }

  /**
   * @deprecated moving to getByStatuses so can provide more than one status
   * @param  {string} status
   */
  static getByStatus(status: string) {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.status === status);
    });
  }

  static getByStatusesActiveJobs(statuses: string[]) {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter(
        (item) =>
          item.status !== null &&
          statuses.includes(item.status) &&
          !item.metadata!.stagedBool &&
          !item.metadata!.tempJobBool &&
          !item.metadata!.testJobBool
      );
    });
  }

  static getByStatuses(statuses: string[]) {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.status !== null && statuses.includes(item.status));
    });
  }

  static getStagedJobs() {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.metadata!.stagedBool && !item.metadata!.archiveBool);
    });
  }

  static getTestingJobs() {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.metadata!.testJobBool && !item.metadata!.archiveBool);
    });
  }

  static sortJobByArrayDateValues(
    fieldName: string,
    dateOption: "start" | "stop",
    items: Model[],
    direction: "asc" | "desc",
    empty: "start" | "end"
  ) {
    return [...items].sort((a, b) => {
      const datesA = a.dates as unknown as Record<string, ArrayDate[]>;
      const datesB = b.dates as unknown as Record<string, ArrayDate[]>;
      const dateInputA = datesA[fieldName][datesA[fieldName].length - 1][dateOption];
      const dateInputB = datesB[fieldName][datesB[fieldName].length - 1][dateOption];

      const dateA = Date.parse(dateInputA as unknown as string);
      const dateB = Date.parse(dateInputB as unknown as string);

      // console.log({dateA, dateB})
      if (Number.isNaN(dateA)) {
        return empty === "start" ? -1 : 1;
      }
      if (Number.isNaN(dateB)) {
        return empty === "start" ? 1 : -1;
      }
      if (dateA === dateB) {
        return 0;
      }
      return direction === "asc" ? (dateA > dateB ? 1 : -1) : dateA < dateB ? 1 : -1;
    });
  }

  static sortByJobDateOutProperties(items: Model[], direction: "asc" | "desc", empty: "start" | "end") {
    return [...items].sort((a, b) => {
      const dateA = a.dates!.estimatedShipDate
        ? a.dates!.estimatedShipDate
        : a.dates!.shipmentDates
          ? a.dates!.shipmentDates[0].start
          : null;
      const dateB = b.dates!.estimatedShipDate
        ? b.dates!.estimatedShipDate
        : b.dates!.shipmentDates
          ? b.dates!.shipmentDates[0].start
          : null;

      // console.log({dateA, dateB})
      if (Number.isNaN(dateA)) {
        return empty === "start" ? -1 : 1;
      }
      if (Number.isNaN(dateB)) {
        return empty === "start" ? 1 : -1;
      }
      if (dateA === dateB) {
        return 0;
      }
      return direction === "asc" ? (dateA! > dateB! ? 1 : -1) : dateA! < dateB! ? 1 : -1;
    });
  }

  /**
   * Sort Fullfilling/Building Jobs By the assembled by date and the estimated shipment date
   * @param items Array of jobs with the building/fullfilling status
   * @param direction direction of the sort
   * @param empty
   */
  static sortFullingJobByDateValues(items: Model[], direction: "asc" | "desc", empty: "start" | "end") {
    return [...items].sort((a, b) => {
      const completeDateA = a.dates!.shopCompleteByDate;
      const completeDateB = b.dates!.shopCompleteByDate;
      const estimateDateA = a.dates!.estimatedShipDate;
      const estimateDateB = b.dates!.estimatedShipDate;

      // console.log({completeDateA, completeDateB})
      if (Number.isNaN(completeDateA)) {
        return empty === "start" ? -1 : 1;
      }
      if (Number.isNaN(completeDateB)) {
        return empty === "start" ? 1 : -1;
      }
      if (completeDateA === completeDateB) {
        return direction === "asc"
          ? estimateDateA! > estimateDateB!
            ? 1
            : -1
          : estimateDateA! < estimateDateB!
            ? 1
            : -1;
      }
      return direction === "asc" ? (completeDateA! > completeDateB! ? 1 : -1) : completeDateA! < completeDateB! ? 1 : -1;
    });
  }

  constructor() {
    super();
  }

  ngxsAfterBootstrap({ dispatch, patchState }: StateContext<StateModel>): void {
    patchState({ loading: true });
    this.store
      .select(AuthState.hasGapiToken)
      .pipe(
        appAfterBootstrap(),
        tap(() => dispatch(new StateActions.GetAll()))
      )
      .subscribe();
  }

  @Action(StateActions.FilterList, { cancelUncompleted: true })
  filterList({ patchState }: StateContext<StateModel>, { payload: filterText }: StateActions.FilterList) {
    patchState({ filterText });
  }

  @Action(StateActions.GetAll, { cancelUncompleted: true })
  getAll({ dispatch, patchState }: StateContext<StateModel>) {
    patchState({ loading: true });
    return this.db.collection$().pipe(
      map((items) => items.map((item) => new Model(item))),
      tap((items) =>
        patchState({
          loading: false,
          items,
        })
      ),
      mergeMap(() => dispatch(new StateActions.StartCollectionSheetWatcher()))
    );
  }

  @Action(StateActions.StartCollectionSheetWatcher)
  async watchCollection({ dispatch, patchState }: StateContext<StateModel>) {
    patchState({ loading: true });
    return this.db.docs$().pipe(mergeMap((items) => dispatch(new StateActions.UpdateFromSheetWatcher(items))));
  }

  @Action(StateActions.UpdateFromSheetWatcher)
  updateState(
    { patchState, setState }: StateContext<StateModel>,
    { payload: items }: StateActions.UpdateFromSheetWatcher
  ) {
    items.map((_item) => {
      _item = new Model(_item);
      setState(
        patch<StateModel>({
          items: upsertItem<Model>((item) => item.uid === _item.uid, _item),
        })
      );
    });
    patchState({ loading: false });
  }

  /**
   *
   * Below are actions that dispatch CRUD operations to the db handling the collection
   *
   */

  @Action(StateActions.Update)
  async update({ getState, patchState }: StateContext<StateModel>, { payload }: StateActions.Update) {
    patchState({ loading: true });

    const { rowIndex = null, ...currentState } = getState().items.find((item) => item.jobNumber === payload.jobNumber)!;
    const mergedItem = {
      ...currentState,
      ...payload,
      rowIndex,
    } as Model;

    const item = Model.toSpreadsheet(mergedItem);

    await this.db.upsertWithSheetsApi(item.uid!, item);

    // try {
    //   await this.#updateJobByNumber.mutateAsync({
    //     jobNumber: payload.jobNumber,
    //     // @ts-ignore
    //     projectId: payload?.project.id,
    //     // @ts-ignore
    //     projectNumber: payload?.project.projectNumber,
    //     addresses: payload.addresses,
    //     billing: payload.billing,
    //     dates: payload.dates,
    //     description: payload.description,
    //     folderId: payload.folderId,
    //     holeSection: payload.holeSection,
    //     metadata: payload.metadata,
    //     notes: payload.notes,
    //     shippingContainer: payload.shippingContainer,
    //     status: payload.status,
    //     uid: payload.uid,
    //   });
    // } catch (e) {
    //   console.error('Error upserting job to dataconnect:', e);
    // }

    // try {
    //   await this.#updateJobMetadata.mutateAsync({
    //     // @ts-ignore
    //     jobId: payload.id,
    //     ...payload.metadata,
    //   });
    // } catch (e) {
    //   console.error('Error upserting job metadata to dataconnect:', e);
    // }

    patchState({ loading: false });
  }

  @Action(StateActions.Create)
  async create({ patchState }: StateContext<StateModel>, { payload }: StateActions.Create) {
    patchState({ loading: true });

    const item = Model.toSpreadsheet(payload);

    // console.log("Job Item in State Create Action:", item);
    await this.db.upsertWithSheetsApi(undefined, item);

    patchState({ loading: false });
  }

  @Action(StateActions.ToSheetsApi.BatchUpdate)
  async batchUpdate({ patchState }: StateContext<StateModel>, { payload }: StateActions.ToSheetsApi.BatchUpdate) {
    patchState({ loading: true });

    this.db.batchUpdateWithSheetsApi(payload);

    patchState({ loading: false });
  }

  @Action(StateActions.Delete)
  async delete({ patchState }: StateContext<StateModel>, { payload }: StateActions.Delete) {
    patchState({ loading: true });

    const item = payload;
    await this.db.deleteFile(item.folderId!);
    await this.db.delete(item.uid!);

    patchState({ loading: false });
  }
}
